import { Configuration, LogLevel, RedirectRequest } from '@azure/msal-browser';

/**
 * Base origin the redirect bridge page is served from. The bridge page
 * (redirect.html) must be registered as a SPA redirect URI in Entra ID:
 *   <origin>/redirect.html
 */
const APP_ORIGIN = import.meta.env.VITE_REDIRECT_URI || window.location.origin;
export const REDIRECT_URI = `${APP_ORIGIN.replace(/\/$/, '')}/redirect.html`;

/**
 * MSAL Configuration
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID || 'common'}`,
    redirectUri: REDIRECT_URI,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          case LogLevel.Info:
            // Suppress info logs in production
            if (import.meta.env.DEV) {
              console.info(message);
            }
            break;
          case LogLevel.Verbose:
            if (import.meta.env.DEV) {
              console.debug(message);
            }
            break;
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
    },
    allowPlatformBroker: false,
  },
};

/**
 * Scopes for backend API access (separate token request)
 */
export const apiRequest: RedirectRequest = {
  scopes: [`api://${import.meta.env.VITE_BACKEND_CLIENT_ID || ''}/access_as_user`],
};

/**
 * Scopes for login request (Graph API for user info, plus the backend API
 * scope as extra consent so later token calls can be silent)
 */
export const loginRequest: RedirectRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
  extraScopesToConsent: apiRequest.scopes,
};

/**
 * Scopes for Graph API access
 */
export const graphScopes = {
  userRead: ['User.Read'],
  userReadAll: ['User.Read.All'],
};

/**
 * Azure AD Group IDs
 */
export const GROUPS = {
  ONBOARDING_USERS: import.meta.env.VITE_GROUP_ONBOARDING_USERS || '',
  ONBOARDING_ADMINS: import.meta.env.VITE_GROUP_ONBOARDING_ADMINS || '',
} as const;

/**
 * API base URL
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
